import { BankIcon, Button, Card, ErrorCard, FormattedMessageDiv, LoadingCard, PlaidLogo, SecurityIcon, useWizardContext } from '@odmonorepo/core-components';
import { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Col, Collapse, Stack } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import { usePlaidLink, PlaidLinkOptions, PlaidLinkOnSuccess, PlaidLinkOnSuccessMetadata, PlaidLinkError, PlaidLinkOnExit, PlaidLinkOnExitMetadata } from 'react-plaid-link';
import PlaidSuccessCard from './PlaidSuccessCard';
import { useApplication } from 'merchant-api/unified';
import { useParties, usePlaid } from 'merchant-api/spiceroad';
import { steps } from '../LoanApp/LoanAppForm';
import PlaidDisclaimer from './PlaidDisclaimer';

interface PlaidProps { next?: boolean, variation?: string }
export function PlaidCard({ next, variation }: PlaidProps) {
  const intl = useIntl();
  const { isLoadingParties} = useParties();
  const { isLoadingApplication, isErrorApplication } =  useApplication();
  const { getClientInfo, trackPlaid, isLoading } = usePlaid();
  const [errorAlert, setErrorAlert] = useState(false);
  const [linkToken, setLinkToken] = useState('');
  const [plaidSuccess, setPlaidSuccess] = useState(false);
  const { goToStep } = useWizardContext<typeof steps>();

  const onSuccess = useCallback<PlaidLinkOnSuccess>(
    async (public_token: string, metadata: PlaidLinkOnSuccessMetadata) => {
      setErrorAlert(false);
      trackPlaid({
        callback: 'onSuccess',
        metadata: JSON.stringify(metadata),
        publicToken: public_token,
        eventName: 'OPEN',
        error: '',
        variation: ''
      }).then(() => (next ? goToStep("RECEIVED") : setPlaidSuccess(true)))
        .catch(() => {setErrorAlert(true)})
  },[]);

  const onExit = useCallback<PlaidLinkOnExit>(
    async (error: null | PlaidLinkError, metadata: PlaidLinkOnExitMetadata) => {
      setLinkToken('');
      if (error) {
        setErrorAlert(true);
        await trackPlaid({
          callback: 'onExit',
          metadata: JSON.stringify(metadata),
          publicToken: '',
          eventName: 'EXIT',
          error: error ? JSON.stringify(error) : '',
          variation: ''
        });
      }
    }, []
  );
  const config: PlaidLinkOptions = { onSuccess, onExit, token: linkToken};

  const { open } = usePlaidLink(config);

  const launchPlaid = async () => {
    setErrorAlert(false);
    const clientInfo = await getClientInfo();
    clientInfo?.link_token ? setLinkToken(clientInfo?.link_token) : setErrorAlert(true);
  };

  useEffect(() => {
    document.body.style.overflow = 'auto';
    !plaidSuccess && !errorAlert && linkToken && open();
  }, [linkToken, launchPlaid, errorAlert, plaidSuccess]);

  if (isErrorApplication) return <ErrorCard name={intl.formatMessage({ id: 'loading.error' })} />;
  if (isLoadingApplication || isLoadingParties) return <LoadingCard />;

  const bankPageControl = (
    <Stack>
      {!plaidSuccess ? (
        <Card>
          <Collapse in={errorAlert}>
            <div>
              <Alert variant="danger">
                Error connecting bank. Please try again later
              </Alert>
            </div>
          </Collapse>
          <Stack gap={1} className='text-center'>
            <div><Badge bg="info" className="fs-7 p-2">Recommended</Badge></div>
            <FormattedMessageDiv id='plaid.title.M3.true' className="h4" values={{sup: (txt: any) => <sup>{txt}</sup>}} />
            <div className='my-3'><PlaidLogo /></div>
          </Stack>
          <FormattedMessageDiv id="plaid.description" className="mb-5" values={{ link: (txt: any) => <a href="https://my.plaid.com">{txt}</a> }} />
          <Stack className="flex-column" gap={4}>
            <Col className="d-flex shadow-none bg-body p-5 rounded-3 align-items-center">
              <SecurityIcon />
              <FormattedMessageDiv id="plaid.icon.text1" className="ms-6 fs-7" />
            </Col>
            <Col className="d-flex shadow-none bg-body p-5 rounded-3 align-items-center">
              <BankIcon />
              <FormattedMessageDiv id="plaid.icon.text2" className="ms-6 fs-7 h-100" />
            </Col>
          </Stack>
          <FormattedMessageDiv className="fw-bold my-5" id="plaid.reminder" />
          <div className="w-100 d-flex justify-content-center">
            <Button onClick={() => launchPlaid()} loading={isLoading} style={{ width: '100%', maxWidth: '303px' }}>
              <FormattedMessageDiv id="plaid.button" />
            </Button>
          </div>
          <PlaidDisclaimer />
        </Card>
      ) : (
        <PlaidSuccessCard />
      )}
    </Stack>
  );

  const bankPageExperiment = (
    <Stack>
      {!plaidSuccess ? (
        <Card>
          <Collapse in={errorAlert}>
            <div>
              <Alert variant="danger">
                Error connecting bank. Please try again later
              </Alert>
            </div>
          </Collapse>
          <Stack gap={1} className='text-center'>
            <FormattedMessageDiv id='plaid.title.M3.experiment' className="h4" values={{sup: (txt: any) => <sup>{txt}</sup>}} />
          </Stack>
          <FormattedMessageDiv id="plaid.description.experiment" values={{sup: (txt: any) => <sup>{txt}</sup>}} className="mb-5" />
          <Stack className="flex-column" gap={4}>
            <Col className="d-flex shadow-none bg-body p-5 rounded-3 align-items-center">
              <SecurityIcon />
              <FormattedMessageDiv id="plaid.icon.text1.experiment" className="ms-6 fs-7" />
            </Col>
            <Col className="d-flex shadow-none bg-body p-5 rounded-3 align-items-center">
              <BankIcon />
              <FormattedMessageDiv id="plaid.icon.text2" className="ms-6 fs-7 h-100" />
            </Col>
          </Stack>
          <div className="w-100 d-flex justify-content-center my-5">
            <Button onClick={() => launchPlaid()} loading={isLoading} style={{ width: '100%', maxWidth: '303px' }}>
              <FormattedMessageDiv id="plaid.button.experiment" />
            </Button>
          </div>
          <PlaidDisclaimer />
        </Card>
      ) : (
        <PlaidSuccessCard />
      )}
    </Stack>
  );

  return ['language_only', 'pdf_accordion_and_language'].includes(variation || '') ? bankPageExperiment : bankPageControl;
}

export default PlaidCard;
